import type { components } from '@/generated/api-types';
import { KNOWN_ERROR_MESSAGES } from '@/generated/error-messages.gen';

import { toAttachmentInputs } from './attachments';
import { auth } from './auth';

const API_URL = process.env.EXPO_PUBLIC_API_URL;

if (!API_URL) {
  throw new Error('EXPO_PUBLIC_API_URL must be set -- see .env.example.');
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Every screen renders ApiError.message directly (see e.g.
// create-thread's `create.error.message`), so http.Error's raw string
// -- go's http.Error also appends a trailing newline, hence the trim --
// would otherwise reach users verbatim (whole-app critique 2026-09-02).
//
// KNOWN_ERROR_MESSAGES used to be hand-maintained here (87 entries of
// provenance comments and all); it is now generated from openapi.yaml's
// top-level x-error-messages extension (see
// scripts/generate-error-messages.mjs) so a backend error string added
// without a matching entry is a red CI build instead of a silent generic
// fallback at runtime. Two call shapes reach http.Error: literal strings
// and err.Error() passthroughs from auth checks / domain constructors --
// both are keyed in the yaml map. Anything not listed still falls back to
// GENERIC_ERROR_MESSAGE rather than passing raw text through, so an
// error string added on the backend later doesn't silently leak either.

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Check your connection and try again.';

function friendlyErrorMessage(raw: string): string {
  return KNOWN_ERROR_MESSAGES[raw.trim()] ?? GENERIC_ERROR_MESSAGE;
}

// Issue #126 AC4 / #204: no timeout existed on this fetch at all -- a
// hung socket (a cold Cloud Run instance, a dropped connection with no
// TCP RST) left the calling TanStack Query stuck in isPending forever,
// with no error, no retry, nothing to recover from short of leaving
// the screen. 15s is generous for this API's real payload sizes (no
// caller here uploads/downloads anything large) while still resolving
// well inside what a user would wait before assuming something's
// wrong. No caller currently passes its own `signal` (confirmed via
// grep) -- not merged with `AbortSignal.any` here, since that's a
// newer API than `AbortSignal.timeout` and nothing needs it yet; add
// the merge if/when a real caller needs its own cancellation signal
// too.
const REQUEST_TIMEOUT_MS = 15_000;

// Issue #124 AC3: several requests can hit a 401 together (e.g. a
// screen firing multiple queries on mount right as the access token
// expires) -- without this, each one independently calls
// refreshSession() for the SAME expired token, wasting round trips and
// racing on whichever refresh result the app ends up with. Module-level
// so every request() call, across every screen, shares the one
// in-flight refresh.
let refreshInFlight: Promise<boolean> | null = null;

function refreshSessionOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = auth
      .refreshSession()
      .then((session) => session !== null)
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
}

// Attaches the current Supabase session's access token when one
// exists; GET /threads/preview and other public routes work fine
// without it.
//
// Issue #124: a returning user whose access token quietly expired in
// the background (autoRefreshToken in lib/supabase.ts handles the
// common case proactively, on a timer, but not clock skew or a gap
// while the app was suspended) used to hit a 401 with no recovery path
// -- every screen's own "Try again" retry re-sent the SAME expired
// token, an infinite loop of the identical failure. `isRetry` bounds
// this to exactly one retry, on a REAL refreshed token, never a blind
// resend; `data.session !== null` skips the whole dance for a request
// that was never authenticated in the first place (public routes like
// GET /threads/preview have nothing to refresh).
// The body is split this way (rather than everything living in request)
// for exactly one caller: getActivity needs the Response itself (its
// ETag header), not just the parsed body. Everything above -- session
// token, the one shared 401 refresh, the timeout -- still lives here
// once, so the split adds no second copy of the auth dance.
async function requestWithHeaders<T>(
  path: string,
  init?: RequestInit,
  isRetry = false,
): Promise<{ data: T; res: Response }> {
  const session = await auth.getSession();
  const headers = new Headers(init?.headers);

  if (session) {
    headers.set('Authorization', `Bearer ${session.accessToken}`);
  }

  const res = await fetch(`${API_URL}${path}`, { ...init, headers, signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) });

  if (res.status === 401 && !isRetry && session !== null) {
    const refreshed = await refreshSessionOnce();

    if (refreshed) {
      return requestWithHeaders<T>(path, init, true);
    }

    // Refresh itself failed -- the session is genuinely gone, not just
    // stale. Signing out here (rather than letting the 401 bubble up
    // as a plain error) means the SAME reactive nav gate a real
    // sign-out always drives (useAuthState, off Supabase's own
    // onAuthStateChange) routes to sign-in -- one enforcement point,
    // instead of ~20 individual call sites each deciding what a 401
    // means for themselves.
    await auth.signOut();
  }

  // 304 is NOT res.ok (3xx), so this fast-path must precede the throw
  // below -- the badge poller's designed steady state is a 304 on every
  // second tick, and surfacing that as an ApiError would park the query
  // in permanent error state.
  if (res.status === 204 || res.status === 304) {
    return { data: undefined as T, res };
  }

  if (!res.ok) {
    throw new ApiError(res.status, friendlyErrorMessage(await res.text()));
  }

  // RevokeConnectionHandler and DeleteBlockHandler (and any future
  // no-content success response) reply 204 with an empty body -- calling
  // .json() on that throws "Unexpected end of JSON input". Every 2xx
  // response used by this client either has a body or is a 204, so
  // status alone is enough to decide, no need to sniff Content-Length.
  //
  // Only the one etag-aware caller can receive a 304, so the two are not
  // distinguished here.
  return { data: (await res.json()) as T, res };
}

async function request<T>(path: string, init?: RequestInit, isRetry = false): Promise<T> {
  return (await requestWithHeaders<T>(path, init, isRetry)).data;
}

// Issue #143: every response type below used to be hand-declared,
// duplicating backend/openapi.yaml's own schemas with no mechanical
// link between them -- generated/api-types.ts (built by
// `npm run generate-api-types`, checked by CI's openapi-drift-ts job)
// existed but had zero real imports anywhere in the app, so a backend
// contract change could silently drift out of sync with these types
// and CI's own drift gate would never catch it, because nothing here
// actually depended on the artifact it was protecting. Every type
// below is now a direct alias of its generated schema -- the export
// NAMES are unchanged (so no call site elsewhere in the app needed to
// change), only the definition's source of truth moved.
type Schema<Name extends keyof components['schemas']> = components['schemas'][Name];

export type CurrentUser = Schema<'User'>;
export type PreviewThread = Schema<'PreviewThread'>;

// The full shape PATCH /me/profile returns -- see profile.go's
// ownProfileResponse. Deliberately not reused as/for CurrentUser: that
// type describes the account (auth/verification status), this
// describes the profile record, and the two share no fields.
export type OwnProfile = Schema<'OwnProfile'>;
export type GeneratedHandle = Schema<'GeneratedHandle'>;
// { question: string; answer: string } -- OwnProfile.prompts' own item
// shape, pulled out as its own export (issue #238's profile-prompts
// split) since both profile/index.tsx and profile-prompts/index.tsx
// need it now, not just one file's local ad-hoc type.
export type Prompt = Schema<'Prompt'>;

// GET /threads' item shape -- see thread.go's threadResponse. Unlike
// PreviewThread (the unauthenticated, coordinate-free preview shape),
// this carries creatorId/lat/lng because the caller is an
// authenticated, tier0-passed, profiled user who's allowed to see them.
export type ThreadListItem = Schema<'Thread'>;
export type ThreadCategory = Schema<'ThreadCategory'>;

// See thread.go's threadsPageResponse -- {"threads": [...], "nextCursor": string}.
export type ThreadsPage = Schema<'ThreadsPage'>;

// GET /threads/{id}/posts' item shape -- see post.go's postResponse.
// parentId is always null from this app's UI (no nested-reply composer
// in this slice -- see api.createPost), but the field stays nullable
// in the type since the backend allows nesting and a post fetched here
// could have been created with a parent by a future client.
export type PostItem = Schema<'Post'>;

// See post.go's postsPageResponse -- {"items": [...], "nextCursor": string}.
// NOT the same shape as ThreadsPage above: ThreadsPage alone uses the
// older {"threads": [...], "nextCursor": string} convention (a
// pre-existing, deliberately unchanged naming choice); PostsPage shares
// the {"items": [...], "nextCursor": string} shape with
// ConnectionsPage/RevealRequestsPage below instead.
export type PostsPage = Schema<'PostsPage'>;

// GET /profiles/{userId}'s shape -- see profile.go's limitedProfileResponse.
// Deliberately distinct from OwnProfile: no lat/lng/radiusM, ever (server
// never sends them for another user's profile, regardless of connection
// status -- see design.md's "precise coordinates are a server-only
// column" rule). Named OtherProfile here (the openapi.yaml schema
// itself is ProfileView) -- kept the app's own, more descriptive
// existing name rather than renaming every call site to match.
export type OtherProfile = Schema<'ProfileView'>;

// See reveal_request.go's revealRequestResponse. cooldownUntil is
// server-computed (resolvedAt + the decline cooldown), present only
// when status is "declined" -- see lib/reveal-request-status.ts's
// describeCooldown for why the frontend needs this rather than
// re-deriving the cooldown duration itself (issue #151 AC6b).
export type RevealRequest = Schema<'RevealRequest'>;

// See connection.go's connectionResponse. otherUserHandle is absent
// (not empty-string) when the other user has no Profile yet -- see
// connection.go's toConnectionResponse and its
// "OmitsOtherUserHandleWhenNoProfile" test.
export type Connection = Schema<'Connection'>;

// See connection.go's connectionsPageResponse -- {"items": [...], "nextCursor": string},
// same {items, nextCursor} shape as PostsPage above and
// RevealRequestsPage below. NOT the same shape as ThreadsPage, which
// alone uses the older {"threads": [...], "nextCursor": string}
// convention.
export type ConnectionsPage = Schema<'ConnectionsPage'>;

// See reveal_request.go's revealRequestsPageResponse -- {"items": [...], "nextCursor": string},
// same {items, nextCursor} shape as PostsPage/ConnectionsPage above.
// NOT the same shape as ThreadsPage, which alone uses the older
// {"threads": [...], "nextCursor": string} convention.
export type RevealRequestsPage = Schema<'RevealRequestsPage'>;

export type NotificationPreferences = Schema<'NotificationPreferences'>;

// See message.go's messageResponse -- one chat message. Messages only
// exist between two people who have mutually revealed (the Connection is
// the precondition), so this never appears without one.
export type Message = Schema<'Message'>;
export type Attachment = Schema<'Attachment'>;

// See message.go's messagesPageResponse -- {"items": [...], "nextCursor": string},
// same shape as PostsPage/ConnectionsPage/RevealRequestsPage above. NOT
// the same shape as ThreadsPage, which alone uses the older
// {"threads": [...], "nextCursor": string} convention.
export type MessagesPage = Schema<'MessagesPage'>;

// See block.go's blockResponse.
export type Block = Schema<'Block'>;

// See block.go's ListMyBlocksHandler -- the caller's own "people I've
// blocked" list (issue #101), blocker-only (never a block made against
// the caller -- see the backend's own ListBlockedByUser doc comment).
export type BlockedUser = Schema<'BlockedUser'>;

// {"items": [...], "nextCursor": string}, same shape as
// ConnectionsPage/RevealRequestsPage/MessagesPage above.
export type BlockedUsersPage = Schema<'BlockedUsersPage'>;

// See report.go's reportResponse/createReportRequest. targetType/
// severity pulled off the Report schema itself (not hand-duplicated as
// separate literal unions) so a backend enum change can't drift these
// two out of sync with the field they actually describe.
export type ReportTargetType = Schema<'Report'>['targetType'];
export type ReportSeverity = Schema<'Report'>['severity'];

// The runtime-checkable twin of ReportTargetType (issue #126 AC6): the
// type above is erased at runtime, so a deep link's raw route param
// needs an actual value to validate against, not just a TS type. Kept
// literal (not derived from the union) since TS has no built-in way to
// turn a string-literal union back into a runtime array -- three
// literals is not worth a code-generation step.
export const REPORT_TARGET_TYPES: readonly ReportTargetType[] = ['user', 'thread', 'post'];

export function isReportTargetType(value: string): value is ReportTargetType {
  return (REPORT_TARGET_TYPES as readonly string[]).includes(value);
}

export type Report = Schema<'Report'>;

// GET /me/activity's body (issue #395) -- both badge counts in one
// payload, the single query behind every badge in the app.
export type ActivitySummary = Schema<'ActivitySummary'>;

// What getActivity hands back on a 200: the counts plus the strong ETag
// to re-send as If-None-Match next poll. `null` means 304 Not Modified
// -- the expected steady state (identical counts always re-derive the
// identical etag), never an error.
export type ActivityResult = { summary: ActivitySummary; etag?: string };

export const api = {
  me: () => request<CurrentUser>('/me'),
  // GET /me/activity (issue #395) -- the app-wide badge poll. Needs two
  // things request()'s plain return value can't express: the response's
  // ETag header (re-sent next poll as If-None-Match) and a 304 read as
  // "unchanged", not an error -- hence the requestWithHeaders split
  // above rather than a second hand-rolled fetch. Send ifNoneMatch as
  // the etag returned from the previous call; a matching one yields
  // null (keep your last counts), anything else yields the fresh body.
  getActivity: async (ifNoneMatch?: string): Promise<ActivityResult | null> => {
    const { data, res } = await requestWithHeaders<ActivitySummary | undefined>('/me/activity', {
      headers: ifNoneMatch === undefined ? undefined : { 'If-None-Match': ifNoneMatch },
    });
    return data === undefined ? null : { summary: data, etag: res.headers.get('ETag') ?? undefined };
  },
  previewThreads: () => request<PreviewThread[]>('/threads/preview'),
  // ListThreadsHandler 400s with "create a profile before browsing
  // threads" when the caller has no Profile yet -- callers should
  // check `error instanceof ApiError && error.status === 400` and
  // route to profile creation rather than showing this as a generic
  // failure (see threads screen). Cursor-based pagination (`?cursor=`)
  // -- same query-string idiom as listPosts/listMyConnections below --
  // now wired up on the client too (issue #138): the threads screen
  // drives this via useInfiniteQuery for infinite scroll.
  listThreads: (cursor?: string, category?: ThreadCategory) => {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    if (category) params.set('category', category);
    const query = params.toString();
    return request<ThreadsPage>(`/threads${query ? `?${query}` : ''}`);
  },

  // Votes (issue #241): one vote per user per target, PUT replaces the
  // direction, DELETE removes it (idempotent 204 -- no body to decode).
  voteThread: (threadId: string, direction: 'up' | 'down') =>
    request<void>(`/threads/${threadId}/vote`, { method: 'PUT', body: JSON.stringify({ direction }) }),
  removeThreadVote: (threadId: string) => request<void>(`/threads/${threadId}/vote`, { method: 'DELETE' }),
  votePost: (postId: string, direction: 'up' | 'down') =>
    request<void>(`/posts/${postId}/vote`, { method: 'PUT', body: JSON.stringify({ direction }) }),
  removePostVote: (postId: string) => request<void>(`/posts/${postId}/vote`, { method: 'DELETE' }),
  // GetOwnProfileHandler returns the same ownProfileResponse shape as
  // updateProfile -- reuses OwnProfile rather than a redundant type.
  // 404s (as an ApiError with status 404) when the caller has no
  // Profile yet -- callers should treat that the same way as
  // listThreads' 400 (a stable "create a profile first" outcome, not a
  // transient failure), and where the caller only needs this for a
  // non-essential enhancement (e.g. computing a thread's distance),
  // it's fine to just omit that feature on a 404 rather than surface it.
  getOwnProfile: () => request<OwnProfile>('/me/profile'),
  // GenerateHandleHandler (issue #211) -- a candidate handle, not a
  // reservation; it isn't claimed until the caller actually saves a
  // Profile with it via updateProfile. Requires completed signup (404
  // otherwise), same gate as every other /me/* write-adjacent route.
  generateHandle: () => request<GeneratedHandle>('/me/profile/generate-handle'),
  // GetThreadHandler returns the same threadResponse shape as a
  // listThreads item -- reuses ThreadListItem rather than a redundant
  // type. 404s (as an ApiError with status 404) when the thread
  // doesn't exist or was deleted -- callers should branch on that
  // rather than treating it as a generic/retriable failure.
  getThread: (threadId: string) => request<ThreadListItem>(`/threads/${threadId}`),
  // CreateThreadHandler returns the same threadResponse shape as
  // getThread/listThreads -- reuses ThreadListItem rather than a
  // redundant type (see that interface's doc comment).
  createThread: (input: {
    title: string;
    body: string;
    lat: number;
    lng: number;
    category?: ThreadCategory;
    photoBlobIds?: string[];
  }) => {
    const { photoBlobIds = [], ...rest } = input;
    // The first link in the title or body becomes a preview card.
    const attachments = toAttachmentInputs(photoBlobIds, `${rest.title}\n${rest.body}`);

    return request<ThreadListItem>('/threads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(attachments.length > 0 ? { ...rest, attachments } : rest),
    });
  },
  // ListPostsHandler returns a {"items": [...], "nextCursor": string}
  // page, keyset-paginated via ?cursor= -- same wrapping idiom as
  // listThreads/ThreadsPage. This slice doesn't wire up infinite-scroll
  // UI for a thread's replies (out of scope, issue #138 only covers the
  // Threads list) -- but thread-detail's screen needs the COMPLETE
  // reply list (pre-pagination behavior), so it drives this via
  // fetchAllPages's fetchAllPosts helper rather than calling this
  // directly.
  listPosts: (threadId: string, cursor?: string) =>
    request<PostsPage>(`/threads/${threadId}/posts${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
  // parentId is always sent as null -- see PostItem's doc comment.
  createPost: (threadId: string, body: string, photoBlobIds: string[] = []) => {
    const attachments = toAttachmentInputs(photoBlobIds, body);

    return request<PostItem>(`/threads/${threadId}/posts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(attachments.length > 0 ? { body, parentId: null, attachments } : { body, parentId: null }),
    });
  },
  // PATCH /me/profile has no partial-merge semantics (see
  // profile.go's UpdateProfileHandler doc comment) -- every call sends
  // the full desired state, so a caller that doesn't already know the
  // account's current bio/prompts/interests/photos MUST fetch them
  // (getOwnProfile) before calling this, or those fields silently
  // revert to empty on every save (issue #136's review: this was a
  // real, live bug -- editing your handle used to wipe any photos
  // you'd already uploaded, since the old version of this function
  // hardcoded photos: [] unconditionally). bio/prompts/interests/
  // photos default to empty here only for callers with genuinely
  // nothing to preserve (a first-time profile creation), never as a
  // silent fallback for an edit.
  updateProfile: (input: {
    handle: string;
    lat: number;
    lng: number;
    radiusM: number;
    bio?: string;
    prompts?: { question: string; answer: string }[];
    interests?: string[];
    photos?: string[];
    gender?: string;
    ageRange?: string;
    hideDemographics?: boolean;
  }) =>
    request<OwnProfile>('/me/profile', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        handle: input.handle,
        bio: input.bio ?? '',
        prompts: input.prompts ?? [],
        interests: input.interests ?? [],
        photos: input.photos ?? [],
        lat: input.lat,
        lng: input.lng,
        radiusM: input.radiusM,
        gender: input.gender ?? '',
        ageRange: input.ageRange ?? '',
        hideDemographics: input.hideDemographics ?? false,
      }),
    }),
  // Base64-encoded jpeg/png/webp, 5MB max, 6 per account -- validated
  // both client-side (content-limits.ts's MAX_PHOTOS, checked before
  // this is ever called) and server-side (the real enforcement; this
  // client-side check is only faster feedback, per this project's own
  // "backend owns enforcement" rule). Does NOT attach the returned id
  // to the caller's Profile -- see updateProfile's own doc comment;
  // the caller must PATCH /me/profile with the id appended.
  uploadPhoto: (dataBase64: string, mime: string) =>
    request<{ id: string; url: string }>('/me/photos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dataBase64, mime }),
    }),
  // Idempotent on the backend (deleting an already-gone id is a
  // no-op 204) -- does not touch the caller's Profile photos array
  // either; see uploadPhoto's doc comment for the same reason.
  deletePhoto: (id: string) => request<void>(`/photos/${id}`, { method: 'DELETE' }),
  // Idempotent on the backend -- safe to call again if a retry lands
  // after the first attempt actually succeeded.
  signup: (ageAttested: boolean) =>
    request<CurrentUser>('/me', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ageAttested }),
    }),
  // A non-2xx here is a real error (network/server issue). A 2xx
  // response can still carry tier0Status: 'tier0_pending' if the
  // liveness check itself failed -- per the backend handler's own doc
  // comment, that's a normal, retriable outcome, not an HTTP error, so
  // callers must branch on the response body, not just on this
  // promise resolving vs. rejecting.
  submitTier0: (selfieBase64: string) =>
    request<CurrentUser>('/verification/tier0', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selfieBase64 }),
    }),
  // GetProfileHandler -- 404s ("profile not found") if the target has
  // no Profile, or if userId doesn't exist. Distance is present only
  // when the caller has an unspent distance-read allowance left (see
  // ports.DistanceReadLimiter) -- absent, not zero, when unavailable.
  getProfile: (userId: string) => request<OtherProfile>(`/profiles/${userId}`),
  // CreateRevealRequestHandler -- 403 if blocked in either direction,
  // 409 if a pending request already exists between the pair, 429 if
  // still within the decline cooldown for this recipient specifically.
  createRevealRequest: (recipientId: string, threadId: string | null) =>
    request<RevealRequest>('/reveal-requests', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipientId, threadId }),
    }),
  // AcceptRevealRequestHandler -- 403 if the caller isn't the
  // recipient, 404 if the id doesn't exist, 409 if already resolved.
  // The response's connectionId is set on a successful accept.
  acceptRevealRequest: (id: string) => request<RevealRequest>(`/reveal-requests/${id}/accept`, { method: 'POST' }),
  // DeclineRevealRequestHandler -- same error shape as accept.
  declineRevealRequest: (id: string) => request<RevealRequest>(`/reveal-requests/${id}/decline`, { method: 'POST' }),
  // ListMyRevealRequestsHandler returns a {"items": [...], "nextCursor": string}
  // page, keyset-paginated via ?cursor= -- every reveal request where
  // the caller is requester or recipient, most recent first. This
  // slice doesn't wire up infinite-scroll UI for this list (out of
  // scope, issue #138 only covers the Threads list) -- but every
  // consumer of this list (profile-view, reveal-requests screen, the
  // incoming-count hook) needs the COMPLETE list (pre-pagination
  // behavior: an older pending request must not silently vanish), so
  // they drive this via fetchAllPages's fetchAllRevealRequests helper
  // rather than calling this directly.
  listMyRevealRequests: (cursor?: string) =>
    request<RevealRequestsPage>(`/me/reveal-requests${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
  // ListMyConnectionsHandler -- same {"items": [...], "nextCursor": string}
  // page shape as listMyRevealRequests above. Callers read through
  // hooks/use-connections.ts (useConnections / useAllConnections), which
  // own the key, the cache shape, and the retry policy.
  listMyConnections: (cursor?: string) =>
    request<ConnectionsPage>(`/me/connections${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
  // RevokeConnectionHandler -- ALSO creates a Block against the other
  // party (BlockSourceRevoke) -- see this plan's Task 6 for why the UI
  // must never present this as a soft, reversible "unmatch". 403 if
  // the caller isn't a party to the connection, 404 if it doesn't exist.
  revokeConnection: (connectionId: string) => request<void>(`/connections/${connectionId}`, { method: 'DELETE' }),
  // Notifications (issue #397). PUT replaces the whole preferences
  // object -- the server 400s on a missing field.
  getNotificationPreferences: () => request<NotificationPreferences>('/me/notification-preferences'),
  updateNotificationPreferences: (prefs: NotificationPreferences) =>
    request<NotificationPreferences>('/me/notification-preferences', { method: 'PUT', body: JSON.stringify(prefs) }),
  // Idempotent; silences message pushes for one conversation only.
  muteConnection: (connectionId: string) => request<void>(`/connections/${connectionId}/mute`, { method: 'PUT' }),
  unmuteConnection: (connectionId: string) => request<void>(`/connections/${connectionId}/mute`, { method: 'DELETE' }),
  // Messaging (issue #64): a conversation belongs to a Connection, so
  // every route is scoped by connection id. All three run the same
  // participant+block guard, which reports a nonexistent connection, a
  // non-participant, and a blocked pair identically as 404 ("connection
  // not found") -- the route is deliberately not an existence oracle
  // (issue #258), so a caller must treat that 404 as "this conversation
  // is gone", never as a retriable failure. Keyset-paginated via
  // ?cursor=, the same idiom as listPosts/listMyConnections above; the
  // page size is fixed server-side.
  listMessages: (connectionId: string, cursor?: string) =>
    request<MessagesPage>(
      `/connections/${connectionId}/messages${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),
  // 400s on an empty body or one over MAX_MESSAGE_LEN -- the composer
  // checks that limit first for faster feedback, but the backend is the
  // real enforcement. Answers 201 with the created message.
  sendMessage: (connectionId: string, body: string, photoBlobIds: string[] = []) =>
    request<Message>(`/connections/${connectionId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body, attachments: toAttachmentInputs(photoBlobIds, body) }),
    }),
  // Records the caller's read state for this conversation (204, no body).
  // Idempotent server-side -- safe to call again if a retry lands after
  // the first attempt actually succeeded.
  markConversationRead: (connectionId: string) =>
    request<void>(`/connections/${connectionId}/messages/read`, { method: 'POST' }),
  // CreateBlockHandler -- always BlockSourceManual. 400 on a
  // self-target attempt (guard this in the UI too, see Task 8).
  createBlock: (userId: string) =>
    request<Block>('/blocks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    }),
  // DeleteBlockHandler -- always 204, even when the block is a
  // protective (revoke/decline-threshold) source the caller has no
  // right to remove (silent, idempotent no-op by design -- see that
  // handler's own doc comment). Never surface this as "block removed"
  // in the UI without the caller having created it themselves.
  deleteBlock: (userId: string) => request<void>(`/blocks/${userId}`, { method: 'DELETE' }),
  // ListMyBlocksHandler -- same {"items": [...], "nextCursor": string}
  // page shape as listMyRevealRequests/listMyConnections above, but
  // blocker-only (issue #101): every row is something the caller made
  // and can remove via deleteBlock above.
  listMyBlocks: (cursor?: string) =>
    request<BlockedUsersPage>(`/me/blocks${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
  // CreateReportHandler -- 400 on an invalid targetType/empty reason/
  // reason over 5000 chars/invalid severity, 429 if rate-limited.
  createReport: (input: { targetType: ReportTargetType; targetId: string; reason: string; severity: ReportSeverity }) =>
    request<Report>('/reports', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
};
